import { useCallback, useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionGroup, AccordionSummary,
    Alert, Box, Button, Chip, CircularProgress, Divider, Sheet, Stack,
    Tab, TabList, TabPanel, Tabs, Typography, tabClasses,
} from '@mui/joy';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsCampaignApprove, APIAdminAdsCampaignDetail,
    APIAdminAdsCampaignReject, APIAdminAdsCampaignRequestChanges,
    APIAdminAdsCreativeApprove, APIAdminAdsCreativeReject,
    APIAdminAdsPendingCreatives, APIAdminAdsQueue, adminAdsError,
} from '../../../api_admin_ads';
import AdModerationCreativeRow from '../../../components/Ads/AdModerationCreativeRow';
import AdRejectModal from '../../../components/Ads/AdRejectModal';
import { PRICING_LABELS, formatMoney } from '../../../components/Ads/adsConstants';
import AiModerationModeToggle from '../../../components/ui/AiModerationModeToggle';


/** Один элемент очереди: кампания раскрывается и грузит свои объявления. */
function QueueItem({ campaign, canModerate, onDone, onError }) {
    const navigate = useNavigate();
    const { adminToken: token } = useAdmin();

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null); // {kind, id, subject, mode}

    const loadDetail = useCallback(async () => {
        if (detail) return;
        try {
            setLoading(true);
            const res = await APIAdminAdsCampaignDetail(token, campaign.id);
            setDetail(res);
        } catch (e) {
            onError(adminAdsError(e));
        } finally {
            setLoading(false);
        }
    }, [token, campaign.id, detail, onError]);

    const refreshDetail = async () => {
        setDetail(null);
        try {
            const res = await APIAdminAdsCampaignDetail(token, campaign.id);
            setDetail(res);
        } catch (e) { /* уже показали ошибку */ }
    };

    const approveCampaign = async () => {
        setBusy('approve-campaign');
        try {
            await APIAdminAdsCampaignApprove(token, campaign.id);
            onDone(`Кампания «${campaign.name}» одобрена`);
        } catch (e) {
            onError(adminAdsError(e));
        } finally { setBusy(null); }
    };

    const submitReject = async ({ reason, comment }) => {
        const target = rejectTarget;
        setBusy(`${target.kind}-${target.id}`);
        try {
            if (target.kind === 'campaign') {
                if (target.mode === 'changes') {
                    await APIAdminAdsCampaignRequestChanges(token, target.id, { reason, comment });
                    onDone(`Кампания «${campaign.name}» возвращена на доработку`);
                } else {
                    await APIAdminAdsCampaignReject(token, target.id, { reason, comment });
                    onDone(`Кампания «${campaign.name}» отклонена`);
                }
            } else {
                await APIAdminAdsCreativeReject(token, target.id, { reason, comment });
                await refreshDetail();
            }
            setRejectTarget(null);
        } catch (e) {
            onError(adminAdsError(e));
        } finally { setBusy(null); }
    };

    const approveCreative = async (creative) => {
        setBusy(`approve-${creative.id}`);
        try {
            await APIAdminAdsCreativeApprove(token, creative.id);
            await refreshDetail();
        } catch (e) {
            onError(adminAdsError(e));
        } finally { setBusy(null); }
    };

    const creatives = (detail?.campaign?.groups || []).flatMap((g) => g.creatives || []);
    const advertiser = detail?.advertiser;

    return (
        <Accordion onChange={(_, expanded) => expanded && loadDetail()}>
            <AccordionSummary>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    sx={{ width: '100%' }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                            <Typography level="title-sm">{campaign.name}</Typography>
                            <Chip size="sm" variant="soft" color="warning">
                                объявлений на проверке: {campaign.creatives_pending ?? '—'}
                            </Chip>
                        </Stack>
                        <Typography level="body-xs" color="neutral">
                            {campaign.advertiser} · {PRICING_LABELS[campaign.pricing_model]} ·{' '}
                            бюджет {formatMoney(campaign.daily_budget)} ₽/сут ·{' '}
                            подана {campaign.submitted_at
                                ? format(new Date(campaign.submitted_at), 'd MMM, HH:mm', { locale: ru })
                                : '—'}
                        </Typography>
                    </Box>
                </Stack>
            </AccordionSummary>

            <AccordionDetails>
                {loading && (
                    <Stack alignItems="center" sx={{ py: 3 }}>
                        <CircularProgress size="sm" />
                    </Stack>
                )}

                {detail && (
                    <Box>
                        {advertiser && (
                            <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                                <Stack direction="row" spacing={3} flexWrap="wrap">
                                    <Box>
                                        <Typography level="body-xs" color="neutral">Рекламодатель</Typography>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {advertiser.legal_name || advertiser.title || '—'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography level="body-xs" color="neutral">ИНН</Typography>
                                        <Typography level="body-sm">{advertiser.legal_inn || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography level="body-xs" color="neutral">Баланс</Typography>
                                        <Typography level="body-sm">
                                            {formatMoney(advertiser.balance)} ₽
                                        </Typography>
                                    </Box>
                                    {advertiser.is_trusted && (
                                        <Chip size="sm" variant="soft" color="success" sx={{ alignSelf: 'center' }}>
                                            Доверенный аккаунт
                                        </Chip>
                                    )}
                                </Stack>
                            </Sheet>
                        )}

                        <Stack spacing={1.5}>
                            {creatives.map((creative) => (
                                <AdModerationCreativeRow
                                    key={creative.id}
                                    creative={creative}
                                    canModerate={canModerate}
                                    busy={busy}
                                    onApprove={approveCreative}
                                    onReject={(c) => setRejectTarget({
                                        kind: 'creative', id: c.id,
                                        subject: `Объявление «${c.title}»`, mode: 'reject',
                                    })}
                                />
                            ))}
                            {creatives.length === 0 && (
                                <Alert color="warning">
                                    В кампании нет объявлений — отклоните её,
                                    показывать нечего
                                </Alert>
                            )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {canModerate && (
                                <>
                                    <Button
                                        color="success"
                                        startDecorator={<CheckRoundedIcon />}
                                        loading={busy === 'approve-campaign'}
                                        onClick={approveCampaign}
                                    >
                                        Одобрить кампанию
                                    </Button>
                                    <Button
                                        color="warning"
                                        variant="soft"
                                        startDecorator={<EditNoteRoundedIcon />}
                                        onClick={() => setRejectTarget({
                                            kind: 'campaign', id: campaign.id,
                                            subject: `Кампания «${campaign.name}»`, mode: 'changes',
                                        })}
                                    >
                                        На доработку
                                    </Button>
                                    <Button
                                        color="danger"
                                        variant="soft"
                                        startDecorator={<BlockRoundedIcon />}
                                        onClick={() => setRejectTarget({
                                            kind: 'campaign', id: campaign.id,
                                            subject: `Кампания «${campaign.name}»`, mode: 'reject',
                                        })}
                                    >
                                        Отклонить кампанию
                                    </Button>
                                </>
                            )}
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => navigate(`/admin/ads/campaigns/${campaign.id}`)}
                            >
                                Открыть карточку
                            </Button>
                        </Stack>

                        <Alert color="neutral" variant="soft" size="sm" sx={{ mt: 1.5 }}>
                            «Одобрить кампанию» пропускает все объявления со статусом
                            «на модерации». Если часть объявлений плоха — отклоните их
                            по отдельности, остальные продолжат работать.
                        </Alert>
                    </Box>
                )}

                <AdRejectModal
                    open={!!rejectTarget}
                    mode={rejectTarget?.mode}
                    subject={rejectTarget?.subject}
                    saving={!!busy}
                    onClose={() => setRejectTarget(null)}
                    onSubmit={submitReject}
                />
            </AccordionDetails>
        </Accordion>
    );
}


export default function AdminAdsModerationPage() {
    const { adminToken: token, has } = useAdmin();
    const canModerate = has('ads.moderate');

    const [tab, setTab] = useState(0);
    const [campaigns, setCampaigns] = useState([]);
    const [creatives, setCreatives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const [queueRes, creativesRes] = await Promise.all([
                APIAdminAdsQueue(token, { page_size: 50 }),
                APIAdminAdsPendingCreatives(token, { page_size: 50 }),
            ]);
            setCampaigns(queueRes?.items || []);
            setCreatives(creativesRes?.items || []);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить очередь модерации'));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const handleDone = (message) => { setNotice(message); load(); };

    const approveCreative = async (creative) => {
        setBusy(`approve-${creative.id}`);
        try {
            await APIAdminAdsCreativeApprove(token, creative.id);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally { setBusy(null); }
    };

    const submitCreativeReject = async ({ reason, comment }) => {
        setBusy(`reject-${rejectTarget.id}`);
        try {
            await APIAdminAdsCreativeReject(token, rejectTarget.id, { reason, comment });
            setRejectTarget(null);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally { setBusy(null); }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="h4">Очередь модерации</Typography>
                    <Typography level="body-sm" color="neutral">
                        Кампаний ждёт проверки: {campaigns.length} ·
                        отдельных объявлений: {creatives.length}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <AiModerationModeToggle
                        token={token}
                        settingKey="ai_ads_moderation_mode"
                        description="Режим ИИ-модерации рекламы: off/advisor/enforcer"
                        title="ИИ-модерация рекламы"
                    />
                    <Button
                        variant="outlined"
                        color="neutral"
                        startDecorator={<RefreshRoundedIcon />}
                        onClick={load}
                        loading={loading}
                    >
                        Обновить
                    </Button>
                </Stack>
            </Stack>

            {!canModerate && (
                <Alert color="warning" sx={{ mb: 2 }}>
                    У вашей роли нет права <b>ads.moderate</b> — доступен только просмотр.
                </Alert>
            )}

            {notice && (
                <Alert color="success" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="success" onClick={() => setNotice(null)}>
                        Скрыть
                    </Button>
                }>{notice}</Alert>
            )}
            {error && (
                <Alert color="danger" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="danger" onClick={() => setError(null)}>
                        Скрыть
                    </Button>
                }>{error}</Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                <TabList sx={{
                    mb: 2, borderRadius: 'md',
                    [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                        bgcolor: 'primary.softBg', color: 'primary.700',
                    },
                }}>
                    <Tab>Кампании ({campaigns.length})</Tab>
                    <Tab>Отдельные объявления ({creatives.length})</Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0 }}>
                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                    ) : campaigns.length === 0 ? (
                        <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                            <Typography level="title-md">Очередь пуста</Typography>
                            <Typography level="body-sm" color="neutral">
                                Все поданные кампании проверены
                            </Typography>
                        </Sheet>
                    ) : (
                        <AccordionGroup variant="outlined" sx={{ borderRadius: 'md' }}>
                            {campaigns.map((campaign) => (
                                <QueueItem
                                    key={campaign.id}
                                    campaign={campaign}
                                    canModerate={canModerate}
                                    onDone={handleDone}
                                    onError={setError}
                                />
                            ))}
                        </AccordionGroup>
                    )}
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0 }}>
                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                    ) : creatives.length === 0 ? (
                        <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                            <Typography level="title-md">Нет объявлений на проверке</Typography>
                            <Typography level="body-sm" color="neutral">
                                Сюда попадают объявления, изменённые в уже одобренных кампаниях
                            </Typography>
                        </Sheet>
                    ) : (
                        <Stack spacing={1.5}>
                            {creatives.map((creative) => (
                                <AdModerationCreativeRow
                                    key={creative.id}
                                    creative={creative}
                                    campaignName={creative.campaign_name}
                                    advertiser={creative.advertiser}
                                    canModerate={canModerate}
                                    busy={busy}
                                    onApprove={approveCreative}
                                    onReject={(c) => setRejectTarget({
                                        id: c.id, subject: `Объявление «${c.title}»`,
                                    })}
                                />
                            ))}
                        </Stack>
                    )}
                </TabPanel>
            </Tabs>

            <AdRejectModal
                open={!!rejectTarget}
                subject={rejectTarget?.subject}
                saving={!!busy}
                onClose={() => setRejectTarget(null)}
                onSubmit={submitCreativeReject}
            />
        </Box>
    );
}